import React, { useState, useRef, useEffect } from "react";
import { STRIP } from "../../../core/theme/reportTheme";

// Channel tiles with nothing to show (nobody seen, or not searched yet) shrink
// to a narrow strip with just the channel's pill on its side, like a side tab.
// Tiles with data share the space. Hovering a strip opens it back
// into a full tile and the others in its group shrink to make room (Scott,
// 17 Oct 2026). Used by Market Overview's tile row and Advertiser Overview's
// channel tiles.

// A strip's share of the width, against 1 for a full tile.
const STRIP_WEIGHT = 0.25;
// How long the slide takes - the same as the CSS transition (STRIP.slot).
const SLIDE_MS = 500;

// slot(key, isStrip) gives a tile's width (flex) and the mouse handlers;
// open(key, isStrip) says whether to draw the full tile (it has data, or it's
// the hovered strip AND it has finished sliding open - so its text never
// wraps in a half-open tile and pushes the page down). Leaving switches back
// to the strip straight away, then it slides closed.
export function useChannelStrips() {
  const [hovered, setHovered] = useState(null); // widening
  const [shown, setShown] = useState(null); // drawn full
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const enter = key => {
    setHovered(key);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setShown(key), SLIDE_MS);
  };
  const leave = key => {
    clearTimeout(timer.current);
    setHovered(current => (current === key ? null : current));
    setShown(current => (current === key ? null : current));
  };

  const open = (key, isStrip) => !isStrip || shown === key;
  const weight = (key, isStrip) => (!isStrip || hovered === key ? 1 : STRIP_WEIGHT);
  const slot = (key, isStrip) => ({
    className: STRIP.slot,
    style: { flexGrow: weight(key, isStrip), flexBasis: 0, minWidth: isStrip ? STRIP.minWidth : 0 },
    onMouseEnter: isStrip ? () => enter(key) : undefined,
    onMouseLeave: isStrip ? () => leave(key) : undefined,
  });
  // A group's width: the sum of its tiles' widths, so groups grow and shrink too.
  const groupWeight = tiles => tiles.reduce((sum, [key, isStrip]) => sum + weight(key, isStrip), 0);
  return { open, slot, groupWeight };
}

// The narrow strip: the channel pill on its side, like a side tab, reading
// top to bottom ("Google Shopping" with "Google" at the top).
export function ChannelStrip({ channel, name, dashed, title }) {
  return (
    <div className={`${STRIP.tile} ${dashed ? STRIP.dashed : STRIP.solid}`} title={title || name}>
      <span className={`${STRIP.pill} ${STRIP.pillColour[channel] || ""}`} style={{ writingMode: "vertical-rl" }}>{name}</span>
    </div>
  );
}
